import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/components/ui/command';
import { Input } from '@/components/ui/input';

/**
 * Agency picker with the search box inside the dropdown.
 *
 * cmdk's own filtering matches against the rendered label, which is useless
 * here: most agencies display under a preferred name written in Chinese while
 * staff search by the English company name. So filtering is done here against
 * each option's `searchText` (company, preferred name, contact, email, phone,
 * id) and cmdk is told to keep its hands off.
 */
export default function AgencyCombobox({
  options = [],
  value = '',
  onChange,
  placeholder = 'No agency',
  emptyLabel = 'No agency',
  disabled = false,
  className = '',
  triggerTestId = 'agency-combobox',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = options.find((option) => option.id === value);
  const needle = query.trim().toLowerCase();
  const filtered = useMemo(
    () => (needle
      ? options.filter((option) => String(option.searchText || option.label || '').toLowerCase().includes(needle))
      : options),
    [options, needle],
  );

  const pick = (id) => {
    onChange?.(id);
    setOpen(false);
    setQuery('');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => { setOpen(next); if (!next) setQuery(''); }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={triggerTestId}
          className={cn('h-auto min-h-10 w-full justify-between py-2 font-normal', !selected && 'text-[#062B1A]/50', className)}
        >
          <span className="min-w-0 text-left whitespace-normal"><span className="block">{selected ? selected.label : (value ? `Unavailable agency (${value})` : placeholder)}</span>{selected?.detail && <span className="block text-xs text-[#062B1A]/55">{selected.detail}</span>}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <div className="flex items-center gap-2 border-b px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search company, contact, email, phone..."
              aria-label="Search agencies"
              data-testid="agency-combobox-search"
              className="h-10 border-0 px-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
            />
          </div>
          <CommandList>
            <CommandEmpty>No agency matches that search.</CommandEmpty>
            <CommandGroup>
              <CommandItem value="__none__" onSelect={() => pick('')} className="cursor-pointer">
                <Check className={cn('mr-2 h-4 w-4', value ? 'opacity-0' : 'opacity-100')} />
                <span className="text-[#062B1A]/60">{emptyLabel}</span>
              </CommandItem>
              {filtered.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.id}
                  onSelect={() => pick(option.id)}
                  className="cursor-pointer"
                >
                  <Check className={cn('mr-2 h-4 w-4 shrink-0', value === option.id ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0">
                    <span className="block whitespace-normal break-words">{option.label}</span>
                    {option.detail && (
                      <span className="block whitespace-normal break-words text-xs text-[#062B1A]/45">{option.detail}</span>
                    )}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
